import { useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { taskCommentsApi } from './api-handlers';
import { taskCommentsQueryKeys } from './query-keys';
import { TASK_COMMENTS_POLLING_INTERVAL } from '../model/config';

import type { TTaskCommentsData } from '../model/types';

interface UseGetTaskCommentsPollingParams {
    taskId: string;
    stateToken?: string;
    enabled: boolean;
}

export const useGetTaskCommentsPolling = ({
    taskId,
    stateToken,
    enabled,
}: UseGetTaskCommentsPollingParams) => {
    const queryClient = useQueryClient();
    const [currentStateToken, setCurrentStateToken] = useState(stateToken);

    useEffect(() => {
        setCurrentStateToken(stateToken);
    }, [stateToken]);

    const pollingQuery = useQuery({
        queryKey: taskCommentsQueryKeys.poll(taskId),
        queryFn: () => taskCommentsApi.pollTaskComments(taskId, currentStateToken),
        enabled: enabled && Boolean(taskId),
        refetchInterval: TASK_COMMENTS_POLLING_INTERVAL,
        refetchOnMount: false,
        refetchIntervalInBackground: false,
    });
    const { data: pollingData, isFetchedAfterMount, isSuccess } = pollingQuery;

    useEffect(() => {
        if (!isFetchedAfterMount || !isSuccess || !pollingData) return;

        setCurrentStateToken(pollingData.stateToken);

        if (!pollingData.hasUpdates) return;

        queryClient.setQueryData<TTaskCommentsData>(taskCommentsQueryKeys.list(taskId), (data) => {
            if (!data) return data;

            return {
                ...data,
                stateToken: pollingData.stateToken,
                comments: pollingData.comments,
            };
        });
    }, [taskId, pollingData, isFetchedAfterMount, isSuccess, queryClient]);

    return pollingQuery;
};
